import { Box } from "@mui/joy";
import { CellContext, createColumnHelper } from "@tanstack/react-table";
import { differenceInDays } from "date-fns";

import formatLifetime from "@/utils/formatLifetime";
import shortDateFormat from "@/utils/shortDateFormat";
import { getTraitLabel } from "@/utils/traits";

import { Route } from "../../core/types";
import RouteLabelChip from "../RouteLabels/RouteLabelChip";
import { CircuitCell, CompetitionCategoriesCell, CompetitionCell } from "./CircuitCell";
import MapName from "./MapName";
import { RouteNameCell } from "./RouteName";
import { SelectionCell, SelectionHeaderCell } from "./SelectionCells";
import { SettersCell } from "./SetterName";

// Fades out the last 20px of a cell's contents, so chips that don't fit fade into whatever
// background the row has, including when it's selected.
const fadeOverflowSx = {
  maskImage: "linear-gradient(to right, black calc(100% - 20px), transparent)",
} as const;

const TraitLabelCell = ({ value }: { value: string }) => {
  return (
    <Box
      sx={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        display: "block",
      }}
    >
      {value}
    </Box>
  );
};

const columnHelper = createColumnHelper<Route>();

export const circuitColumn = columnHelper.accessor("circuit.uuid", {
  header: "Circuit",
  id: "circuit",
  cell: (props) => {
    if (!props.row.original.circuit) {
      if (props.row.original.competitionRouteGroups.length) {
        const competitionName = props.row.original.competitionRouteGroups[0].competitionName;
        return <CompetitionCell name={competitionName} />;
      }
    } else {
      return (
        <CircuitCell
          name={props.row.original.circuit.name || ""}
          colors={props.row.original.circuit.colors ?? ["#FFFFFF"]}
        />
      );
    }
  },
  sortingFn: (rowA, rowB) => {
    return (
      rowA.original.circuit?.name ||
      rowA.original.competitionRouteGroups?.[0]?.competitionName ||
      ""
    ).localeCompare(
      rowB.original.circuit?.name ||
        rowB.original.competitionRouteGroups?.[0]?.competitionName ||
        "",
    );
  },
});

export const lineColumn = columnHelper.accessor((x) => x.startLine?.uuid, {
  header: "Line",
  id: "startLine",
  meta: {
    filterName: "startLine",
  },
  cell: (props) => {
    if (props.row.original.competitionRouteGroups.length) {
      const competitionName = props.row.original.competitionRouteGroups[0].competitionName;
      return <CompetitionCell name={competitionName} />;
    }
    if (!props.row.original.startLine) {
      return "–";
    }
    return props.row.original.startLine.name;
  },
  sortingFn: (rowA, rowB) => {
    return (rowA.original.startLine?.name || "").localeCompare(
      rowB.original.startLine?.name || "",
      undefined,
      {
        numeric: true,
        sensitivity: "base",
      },
    );
  },
});

export const competitionCategoriesColumn = columnHelper.accessor(
  (x) =>
    x.competitionRouteGroups
      .map((x) => x.categories.join(","))
      .join(",")
      .split(","),
  {
    header: "Categories",
    id: "competitionCategories",
    cell: (props) => {
      if (!props.row.original.competitionRouteGroups?.length) {
        return null;
      }
      return (
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            overflow: "hidden",
            width: "100%",
            ...fadeOverflowSx,
          }}
        >
          <CompetitionCategoriesCell
            competitionUuid={props.row.original.competitionRouteGroups[0].competition}
            categoryUuids={props.getValue()}
          />
        </Box>
      );
    },
    meta: {
      filterName: "competitionCategories",
    },
    enableColumnFilter: true,
    size: 200,
  },
);

export const selectionColumn = columnHelper.display({
  id: "selected",
  size: 34,
  header: SelectionHeaderCell,
  cell: SelectionCell,
  enableColumnFilter: false,
});

export const routeColumn = columnHelper.accessor("uuid", {
  id: "name",
  header: "Route",
  cell: (props) => <RouteNameCell route={props.row.original} />,
  size: 110,
  sortDescFirst: false,
  enableSorting: true,
  enableColumnFilter: false,
  sortingFn: (rowA, rowB) => {
    // Sort by circuit, then by circuit number
    const a = rowA.original;
    const b = rowB.original;
    if (a.circuit && b.circuit) {
      if (a.circuit.uuid !== b.circuit.uuid) {
        return (a.circuit.name || "").localeCompare(b.circuit.name || "");
      }
      return (a.circuit.number || 0) - (b.circuit.number || 0);
    }
    if (a.startLine && b.startLine) {
      if (a.startLine.uuid !== b.startLine.uuid) {
        return a.startLine.name.localeCompare(b.startLine.name);
      }
      return (a.gradeInternal || 0) - (b.gradeInternal || 0);
    }
    return 0;
  },
  meta: {
    filterName: "holdColors",
  },
});

export const archiveRouteColumn = {
  ...routeColumn,
  cell: (props: CellContext<Route, string>) => (
    <RouteNameCell route={props.row.original} showTagWarning={false} />
  ),
};

export const gradeColumn = columnHelper.accessor("gradeValue", {
  header: "Grade",
  id: "grade",
  size: 100,
  sortingFn: (rowA, rowB) => {
    if (rowA.original.gradeInternal === rowB.original.gradeInternal) {
      return 0;
    }
    return (rowA.original.gradeInternal || 0) > (rowB.original.gradeInternal || 0) ? 1 : -1;
  },
});

export const setterColumn = columnHelper.accessor(
  (row) => row.setters.map((setter) => setter.uuid),
  {
    id: "setters",
    header: "Setters",
    cell: (props) => <SettersCell setters={props.row.original.setters} />,
  },
);

export const mapColumn = columnHelper.accessor("map", {
  header: "Map",
  id: "map",
  cell: (props) => {
    return props.row.original.map ? <MapName mapUuid={props.row.original.map} /> : "–";
  },
  enableColumnFilter: false,
});

export const zoneColumn = columnHelper.accessor("zone.uuid", {
  id: "zone",
  header: "Zone",
  cell: (props) => {
    if (!props.row.original.zone) {
      return "–";
    }
    return props.row.original.zone.name;
  },
  size: 100,
});

export const sportZoneColumn = columnHelper.accessor("zone.uuid", {
  id: "zone",
  header: "Sector",
  cell: (props) => {
    if (!props.row.original.zone) {
      return "–";
    }
    return props.row.original.zone.name;
  },
  size: 100,
});

export const ascentStyleColumn = columnHelper.accessor(
  (row) => {
    if (row.autoBelay) {
      return "Auto belay";
    }
    if (row.canLead) {
      return "Lead + Toprope";
    }
    return "Toprope only";
  },
  {
    header: "Ascent style",
    id: "ascentStyle",
    cell: (props) => {
      return props.getValue();
    },
    size: 125,
    meta: {
      filterName: "ascentStyle",
    },
  },
);

export const startColumn = columnHelper.accessor("startTime", {
  header: "Set date",
  id: "startTime",
  cell: (props) => {
    if (!props.row.original.startTime) {
      return "–";
    }
    return shortDateFormat(new Date(props.row.original.startTime));
  },
  size: 120,
  meta: {
    filterName: "startTime",
  },
});

export const ageColumn = columnHelper.accessor(
  (row) => (row.startTime ? -1 * new Date(row.startTime).getTime() : 0),
  {
    id: "age",
    header: "Age",
    cell: (props) => {
      return differenceInDays(new Date(), new Date(-props.getValue())) + "d";
    },
    size: 85,
  },
);

export const scheduledStripColumn = columnHelper.accessor("scheduledEndTime", {
  header: "Strip date",
  id: "scheduledEndTime",
  cell: (props) => {
    if (!props.row.original.scheduledEndTime) {
      return "–";
    }
    return shortDateFormat(new Date(props.row.original.scheduledEndTime));
  },
  size: 120,
  meta: {
    filterName: "timeLeft",
  },
});

export const stripColumn = columnHelper.accessor("endTime", {
  header: "Strip date",
  id: "endTime",
  cell: (props) => {
    if (!props.row.original.endTime) {
      return "–";
    }
    return shortDateFormat(new Date(props.row.original.endTime));
  },
  size: 120,
  meta: {
    filterName: "endTime",
  },
});

export const lifetimeColumn = columnHelper.accessor(
  (row) =>
    row.endTime && row.startTime
      ? new Date(row.endTime).getTime() - new Date(row.startTime).getTime()
      : Infinity,
  {
    header: "Lifetime",
    id: "lifetime",
    cell: (props) => {
      const value = props.getValue();
      return formatLifetime(value);
    },
    size: 100,
    meta: {
      filterName: "lifetime",
    },
  },
);

export const starRatingColumn = columnHelper.accessor("feedback.averageStarRating", {
  id: "starRating",
  header: "Avg. rating",
  cell: (props) => {
    if (props.row.original.feedback?.averageStarRating) {
      return `${props.row.original.feedback.averageStarRating.toFixed(1)} (${
        props.row.original.feedback.counts.starRatingsCount
      } ratings)`;
    }
    return "–";
  },
  size: 120,
});

export const gradeFeedbackColumn = columnHelper.accessor(
  (route) => {
    const hardCount = route.feedback?.counts.difficulty["1"] ?? 0;
    const easyCount = route.feedback?.counts.difficulty["-1"] ?? 0;
    const okCount = route.feedback?.counts.difficulty["0"] ?? 0;
    const totalVotes = hardCount + easyCount + okCount;

    // Net directional consensus (easy = -1, ok = 0, hard = +1), made
    // direction-agnostic so routes that are strongly mis-graded *either* way
    // float to the top while genuine disagreement (e.g. 3 hard / 3 easy)
    // cancels out. The +3 prior shrinks low-vote routes toward 0, so a route
    // needs both consensus and enough votes to rank highly. No votes -> 0.
    return Math.abs(hardCount - easyCount) / (totalVotes + 2);
  },
  {
    id: "gradeFeedback",
    header: "Grade feedback",
    cell: (props) => {
      const hardCount = props.row.original.feedback?.counts.difficulty["1"] ?? 0;
      const easyCount = props.row.original.feedback?.counts.difficulty["-1"] ?? 0;
      const okCount = props.row.original.feedback?.counts.difficulty["0"] ?? 0;

      const result = [];
      if (hardCount) {
        result.push(`⬆️\u2009${hardCount}`);
      }
      if (easyCount) {
        result.push(`⬇️\u2009${easyCount}`);
      }
      if (okCount) {
        result.push(`🆗\u2009${okCount}`);
      }
      if (!result.length) {
        return "–";
      }
      return result.join("\u2002");
    },
    size: 120,
  },
);

// Climber comments waiting in the staff inbox. Sorts most first, and the
// "Has comments" filter in the filter sidebar narrows the table to routes with any.
export const commentsColumn = columnHelper.accessor((route) => route.pendingCommentsCount ?? 0, {
  id: "comments",
  header: "Comments",
  cell: (props) => props.getValue() || "–",
  size: 100,
  meta: {
    filterName: "hasComments",
  },
});

export const stylesColumn = columnHelper.display({
  id: "styles",
  header: "Styles",
  cell: (props) => {
    const styles = props.row.original.styles;
    if (!styles) {
      return "–";
    }
    return <TraitLabelCell value={styles.map((v) => getTraitLabel("styles", v)).join(", ")} />;
  },
  size: 200,
});

export const skillsColumn = columnHelper.display({
  id: "skills",
  header: "Skills",
  cell: (props) => {
    const skills = props.row.original.skills;
    if (!skills) {
      return "–";
    }
    return <TraitLabelCell value={skills.map((v) => getTraitLabel("skills", v)).join(", ")} />;
  },
  size: 200,
});

export const holdsColumn = columnHelper.display({
  id: "holds",
  header: "Holds",
  cell: (props) => {
    const holds = props.row.original.holds;
    if (!holds) {
      return "–";
    }
    return <TraitLabelCell value={holds.map((v) => getTraitLabel("holds", v)).join(", ")} />;
  },
  size: 200,
});

export const labelsColumn = columnHelper.accessor(
  (row) => row.labels.map((label) => label.name).join(", "),
  {
    id: "labels",
    header: "Labels",
    cell: (props) => {
      const labels = props.row.original.labels;
      if (!labels.length) {
        return "–";
      }
      return (
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            overflow: "hidden",
            width: "100%",
            ...fadeOverflowSx,
          }}
        >
          {labels.map((label) => (
            <RouteLabelChip
              key={label.uuid}
              size="sm"
              name={label.name}
              groupName={label.group?.name}
              sx={{ flexShrink: 0 }}
            />
          ))}
        </Box>
      );
    },
    meta: {
      filterName: "labels",
    },
    size: 240,
  },
);

export const heightColumn = columnHelper.display({
  id: "height",
  header: "Height",
  cell: (props) => {
    return props.row.original.height ? `${props.row.original.height / 100}m` : "–";
  },
  size: 100,
});

export const quickdrawCountColumn = columnHelper.display({
  id: "quickdrawCount",
  header: "Quickdraws",
  cell: (props) => {
    return props.row.original.quickdrawCount || "–";
  },
  size: 100,
});

export const ticksColumn = columnHelper.accessor("successesCount", {
  header: "# Ticks",
  id: "successesCount",
  cell: (props) => {
    return props.row.original.successesCount ?? "–";
  },
  size: 100,
});
